// App Token Management Service
class AppTokenManager {
  constructor() {
    this.token = null;
    this.refreshPromise = null;
    this.isRefreshing = false;
  }

  // Obtém o token atual do localStorage
  getToken() {
    if (this.token) return this.token;

    const stored = localStorage.getItem('omnidesk_app_token');
    if (stored) {
      try {
        const tokenData = JSON.parse(stored);
        if (tokenData.expires_at && new Date(tokenData.expires_at) > new Date()) {
          this.token = tokenData.app_token;
          return this.token;
        } else {
          localStorage.removeItem('omnidesk_app_token');
        }
      } catch (e) {
        localStorage.removeItem('omnidesk_app_token');
      }
    }
    return null;
  }

  // Armazena o token no localStorage
  setToken(tokenData) {
    this.token = tokenData.app_token;
    localStorage.setItem('omnidesk_app_token', JSON.stringify(tokenData));
  }

  // Remove o token
  clearToken() {
    this.token = null;
    localStorage.removeItem('omnidesk_app_token');
  }

  // Obtém um novo token do servidor
  async fetchNewToken() {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this._requestNewToken();

    try {
      const tokenData = await this.refreshPromise;
      this.setToken(tokenData);
      return tokenData.app_token;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  async _requestNewToken() {
    try {
      const response = await fetch('/api/v1/app_tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching app token:', error);
      throw error;
    }
  }

  // Verifica se o token está próximo do vencimento (menos de 15 minutos)
  isTokenExpiringSoon() {
    const stored = localStorage.getItem('omnidesk_app_token');
    if (!stored) return true;

    try {
      const tokenData = JSON.parse(stored);
      const expiresAt = new Date(tokenData.expires_at);
      const now = new Date();
      const fifteenMinutes = 15 * 60 * 1000;

      return (expiresAt.getTime() - now.getTime()) < fifteenMinutes;
    } catch (e) {
      return true;
    }
  }

  // Garante que temos um token válido
  async ensureValidToken() {
    const currentToken = this.getToken();

    if (!currentToken || this.isTokenExpiringSoon()) {
      return await this.fetchNewToken();
    }

    return currentToken;
  }
}

// Instância global do gerenciador de tokens
const appTokenManager = new AppTokenManager();

// Interceptor para adicionar token nas requisições
export const setupAppTokenInterceptor = (axiosInstance) => {
  // Request interceptor - adiciona o token
  axiosInstance.interceptors.request.use(
    async (config) => {
      try {
        // Pula validação para endpoints específicos
        const skipTokenPaths = [
          '/api/v1/app_tokens',
          '/auth/',
          '/webhooks/',
          '/public/api/'
        ];

        const shouldSkipToken = skipTokenPaths.some(path =>
          config.url?.includes(path)
        );

        if (!shouldSkipToken && config.url?.startsWith('/api/v1/')) {
          const token = await appTokenManager.ensureValidToken();
          if (token) {
            config.headers['X-App-Token'] = token;
          }
        }
      } catch (error) {
        console.error('Error adding app token to request:', error);
        // Continua com a requisição mesmo se houver erro no token
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - gerencia renovação de token e erros
  axiosInstance.interceptors.response.use(
    (response) => {
      // Verifica se há um token renovado no header da resposta
      const refreshedToken = response.headers['x-refreshed-app-token'];
      if (refreshedToken) {
        const tokenData = {
          app_token: refreshedToken,
          expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString() // 1 hora
        };
        appTokenManager.setToken(tokenData);
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Se o erro é 401 e é relacionado ao app token
      if (error.response?.status === 401 &&
          error.response?.data?.code === 'INVALID_APP_TOKEN' &&
          !originalRequest._retry) {

        originalRequest._retry = true;

        try {
          // Limpa o token atual e obtém um novo
          appTokenManager.clearToken();
          const newToken = await appTokenManager.fetchNewToken();

          // Refaz a requisição original com o novo token
          originalRequest.headers['X-App-Token'] = newToken;
          return axiosInstance(originalRequest);
        } catch (tokenError) {
          console.error('Failed to refresh app token:', tokenError);
          // Se não conseguir renovar o token, limpa tudo
          appTokenManager.clearToken();

          // Redireciona para login se necessário
          if (window.location.pathname.startsWith('/app/')) {
            window.location.href = '/auth/sign_in';
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

// Função para inicializar o token manager
export const initializeAppTokenManager = async () => {
  try {
    // Se não temos token válido, tenta obter um
    if (!appTokenManager.getToken() || appTokenManager.isTokenExpiringSoon()) {
      await appTokenManager.fetchNewToken();
    }
  } catch (error) {
    console.warn('Failed to initialize app token:', error);
  }
};

// Exporta o manager para uso externo se necessário
export { appTokenManager };

export default {
  setupAppTokenInterceptor,
  initializeAppTokenManager,
  appTokenManager
};
